import React, { useCallback, useEffect, useRef, useState } from "react";
import { compareSchoolYearDesc } from "../../../../util/schoolYear";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import DynamicTable from "../../../../unicomponents/Tables/DynamicTable";
import DropdownSchoolYear from "../../../../unicomponents/Dropdowns/DropdownSchoolYear";
import DropdownHierarchyOne from "../../../../unicomponents/Dropdowns/DropdownHierarchyOne";
import DropdownHierarchyTwo from "../../../../unicomponents/Dropdowns/DropdownHierarchyTwo";
import DropdownHierarchyThree from "../../../../unicomponents/Dropdowns/DropdownHierarchyThree";
import DropdownUniqueUsersInDisplayData from "../../../../unicomponents/Dropdowns/DropdownUniqueUsersInDisplayData";
import DropdownTermDate from "../../../../unicomponents/Dropdowns/DropdownTermDate";
import useOrganizationSetting from "../../../../hooks/useOrganizationSetting";
import useTermPeriod from "../../../../hooks/useTermPeriod";
import useRegistrationInfo from "../../../../hooks/useRegistrationInfo";
import useLessonStudents from "../../../../hooks/useLessonStudentData";
import useFlexibleData from "../../../../hooks/useFlexibleData";
import { useUIContext } from "../../../../context/UIContext";
import { gradeToConvert100 } from "../../../../util/specialCase";
import { setTermIdByToday } from "../../../../util/dateRelated";
import { generateRegistrationRows } from "../../../../util/dealingArrObj";
import { filterSettingsByStudentsPath } from "../../../../util/specialCase";

export default function RetakeSeniorManagement() {
  const { t } = useTranslation();
  const { setIsLoadingModal, handleToastCenterTop } = useUIContext();
  const location = useLocation();
  const paths = location.pathname.split("/");
  const schoolId = paths[1];

  const [filters, setFilters] = useState({
    schoolYear: null,
    schoolYearId: "",
    hierarchyOneId: "",
    hierarchyTwoId: "",
    hierarchyThreeId: "",
    organPathForSort: "/",
    termId: "",
    selectedStudent: "",
  });

  const [termId, setTermId] = useState("");
  const [studentList, setStudentList] = useState([]);
  const [newSetting, setNewSetting] = useState([]);
  const [lessonStudentsArray, setLessonStudentsArray] = useState([]);
  const [retakeSeniorSettings, setRetakeSeniorSettings] = useState({}); // lesson_id별 Retake/Senior 설정 관리
  const [savedLessons, setSavedLessons] = useState([]); // 저장된 과목들 표시용

  const {
    settingQuery: { data: settingData },
  } = useOrganizationSetting({ schoolid: schoolId });

  const {
    termPeriodQuery: { data: termPeriodData },
  } = useTermPeriod({ schoolid: schoolId });

  const {
    registrationInfoQuery: { data: registrationInfoData },
  } = useRegistrationInfo({ schoolid: schoolId }, { registrationInfo: true });

  const { getAllLessonStudentsByStudentId, updateLessonStudents } =
    useLessonStudents();
  const getAllLessonStudentsByStudentIdRef = useRef(
    getAllLessonStudentsByStudentId
  );

  const {
    flexibleDataQuery: { data: flexibleData },
  } = useFlexibleData({
    schoolid: schoolId,
    main_category: "convert100",
  });

  // 초기 설정 데이터 처리
  useEffect(() => {
    if (registrationInfoData && settingData) {
      setNewSetting(
        filterSettingsByStudentsPath(registrationInfoData, settingData)
      );
    }
  }, [registrationInfoData, settingData]);

  // 학년도 및 학생 리스트 설정
  useEffect(() => {
    let initialSchoolYear = null;
    let initialSchoolYearId = null;
    let initialData = [];

    if (
      filters.schoolYear === null &&
      newSetting &&
      newSetting.length > 0 &&
      registrationInfoData
    ) {
      initialData = newSetting
        .sort((a, b) => a.hierarchy_level - b.hierarchy_level)
        .sort((a, b) => compareSchoolYearDesc(a.school_year, b.school_year));

      if (initialData.length < 1) {
        alert(t("common.messages.registrationError"));
        return;
      }

      initialSchoolYear = initialData[0].school_year;
      initialSchoolYearId = initialData[0].id;

      setFilters((filters) => ({
        ...filters,
        schoolYear: initialSchoolYear,
        schoolYearId: initialSchoolYearId,
      }));

      if (termPeriodData) {
        setTermIdByToday(termPeriodData, setTermId, initialSchoolYearId);
      }

      if (initialSchoolYear) {
        //학생 리스트
        const sortedRows = generateRegistrationRows(
          registrationInfoData,
          newSetting,
          initialSchoolYear,
          filters.organPathForSort
        );
        setStudentList(sortedRows);
      }
    } else {
      //rows 데이터처리
      if (registrationInfoData && newSetting) {
        const sortedRows = generateRegistrationRows(
          registrationInfoData,
          newSetting,
          filters.schoolYear,
          filters.organPathForSort
        );

        setStudentList(sortedRows);
      }
    }
  }, [
    filters.schoolYear,
    filters.organPathForSort,
    registrationInfoData,
    newSetting,
    termPeriodData,
    t,
  ]);

  // Retake/Senior 설정 초기화 함수
  const initializeRetakeSeniorSettings = useCallback((lessonData) => {
    const initialSettings = {};
    lessonData.forEach((lesson) => {
      const lessonId = lesson.lesson_id;
      const existingRetakeSenior =
        lesson.lesson_management?.retake_senior || {};

      initialSettings[lessonId] = {
        type: existingRetakeSenior.type || "", // "retake" or "senior"
        subject_name: existingRetakeSenior.subject_name || "",
        convert100: existingRetakeSenior.convert100 || "", // Letter Grade
        grade: existingRetakeSenior.grade || "", // 총점
      };
    });
    setRetakeSeniorSettings(initialSettings);
  }, []);

  // 선택된 학생의 수업 데이터 가져오기
  useEffect(() => {
    if (filters.selectedStudent && termId) {
      const forWhere = {
        schoolid: schoolId,
        username: filters.selectedStudent,
      };

      getAllLessonStudentsByStudentIdRef.current.mutate(forWhere, {
        onSuccess: (data) => {
          if (data && data.length > 0) {
            const filteredData = data.filter(
              (item) =>
                Number(item.school_year_id) === Number(filters.schoolYearId) &&
                item.lesson?.TermDates?.term_name?.toString() ===
                  filters.termName?.toString()
            );
            setLessonStudentsArray(filteredData);

            // Retake/Senior 설정 초기화
            initializeRetakeSeniorSettings(filteredData);

            handleClearSelection();
          }
        },
        onError: (error) => {
          console.error("수업 데이터 로딩 에러:", error);
          alert(t("common.messages.errorAlert") + error.message);
        },
      });
    } else {
      setLessonStudentsArray([]);
    }
  }, [
    filters.selectedStudent,
    termId,
    filters.schoolYearId,
    schoolId,
    t,
    initializeRetakeSeniorSettings,
    filters.termName,
  ]);

  const handleDropdownChange = (e) => {
    const { name, value } = e.target;
    const selectedOptionText = e.target.selectedOptions[0].text;
    const dataName = e.target.selectedOptions[0].getAttribute("data-name");
    const dataTermName =
      e.target.selectedOptions[0].getAttribute("data-term-name");

    let organPath = "";

    if (!dataName) {
      organPath = "N/A";
    } else {
      organPath = dataName + "/" + value;
    }

    if (name === "0") {
      setFilters((filters) => ({
        ...filters,
        schoolYear: selectedOptionText,
        schoolYearId: value,
        hierarchyOneId: "",
        organPathForSort: "N/A",
        selectedStudent: "",
      }));
      setTermIdByToday(termPeriodData, setTermId, value);
    }

    if (name === "1") {
      setFilters((filters) => ({
        ...filters,
        hierarchyOneId: value,
        hierarchyTwoId: "",
        hierarchyThreeId: "",
        organPathForSort: "N/A",
        selectedStudent: "",
      }));
      setTermIdByToday(termPeriodData, setTermId, filters.schoolYearId, value);
    }

    if (name === "2") {
      setFilters((filters) => ({
        ...filters,
        hierarchyTwoId: value,
        hierarchyThreeId: "",
        organPathForSort: "N/A",
        selectedStudent: "",
      }));
      setTermIdByToday(
        termPeriodData,
        setTermId,
        filters.schoolYearId,
        filters.hierarchyOneId,
        value
      );
    }

    if (name === "3") {
      setFilters((filters) => ({
        ...filters,
        hierarchyThreeId: value,
        organPathForSort: organPath,
        selectedStudent: "",
      }));
    }

    if (name === "students") {
      setFilters((filters) => ({
        ...filters,
        selectedStudent: value,
      }));
    }

    if (name === "termDate") {
      setFilters((filters) => ({
        ...filters,
        termId: value,
        termName: dataTermName,
      }));

      setTermId(value);
    }
  };

  const handleClearSelection = () => {
    // 더 이상 선택 관련 상태가 없음
  };

  // Retake/Senior 설정 변경 핸들러
  const handleRetakeSeniorSettingChange = (lessonId, field, value) => {
    setRetakeSeniorSettings((prev) => {
      const updatedSetting = {
        ...prev[lessonId],
        [field]: value,
      };

      // 총점이 변경될 때 Letter Grade 자동 계산
      if (field === "grade" && flexibleData) {
        if (value && value.trim() !== "") {
          const convert100Data = flexibleData.find(
            (r) => r.sub_category === "직접입력"
          );
          if (convert100Data) {
            const letterGrade = gradeToConvert100(
              value,
              convert100Data.data_value,
              "",
              null // lessonInfo?.extra_attributes?.subjectCategory 대신 null 사용
            );
            updatedSetting.convert100 = letterGrade || "";
          }
        } else {
          // 빈 값이거나 공백만 있는 경우 Letter Grade도 초기화
          updatedSetting.convert100 = "";
        }
      }

      return {
        ...prev,
        [lessonId]: updatedSetting,
      };
    });
  };

  // Retake/Senior 설정 저장 함수
  const handleSaveRetakeSeniorSetting = (lessonStudent) => {
    const lessonId = lessonStudent.lesson_id;
    const currentRetakeSeniorSetting = retakeSeniorSettings[lessonId];

    if (!currentRetakeSeniorSetting) {
      handleToastCenterTop("Retake/Senior 설정 데이터가 없습니다.");
      return;
    }

    // 기존 lesson_management 데이터 보존하면서 retake_senior만 업데이트
    const existingLessonManagement = lessonStudent.lesson_management || {};
    const updatedLessonManagement = {
      ...existingLessonManagement,
      retake_senior: currentRetakeSeniorSetting,
    };

    const updateData = {
      id: lessonStudent.id,
      lesson_management: updatedLessonManagement,
    };

    setIsLoadingModal(true);
    updateLessonStudents.mutate(updateData, {
      onSuccess: (successMessage) => {
        console.log(successMessage);
        setIsLoadingModal(false);
        setSavedLessons((prev) => [...prev, lessonId]);
        handleToastCenterTop("Retake/Senior 설정이 저장되었습니다.");

        // 1초 후 저장 표시 제거
        setTimeout(() => {
          setSavedLessons((prev) => prev.filter((id) => id !== lessonId));
        }, 1000);

        // 데이터 다시 로드하여 최신 상태 반영
        const forWhere = {
          schoolid: schoolId,
          username: filters.selectedStudent,
        };
        getAllLessonStudentsByStudentIdRef.current.mutate(forWhere, {
          onSuccess: (data) => {
            if (data && data.length > 0) {
              const filteredData = data.filter(
                (item) =>
                  Number(item.school_year_id) ===
                    Number(filters.schoolYearId) &&
                  item.lesson?.TermDates?.term_name?.toString() ===
                    filters.termName?.toString()
              );
              setLessonStudentsArray(filteredData);
              initializeRetakeSeniorSettings(filteredData);
            }
          },
        });
      },
      onError: (error) => {
        setIsLoadingModal(false);
        console.error("Retake/Senior 설정 저장 에러:", error);
        alert(t("common.messages.errorAlert") + error.message);
      },
    });
  };

  // DynamicTable 설정
  const tableConfig = {
    style: { fontSize: "14px" },
    headers: [
      {
        label: "과목명",
        style: { width: "200px", textAlign: "left" },
        sortField: "lesson.subject_name",
      },
      {
        label: "시수",
        style: { width: "60px", textAlign: "center" },
      },
      {
        label: "담당교사",
        style: { width: "120px", textAlign: "left" },
      },
      {
        label: "점수",
        style: { width: "60px", textAlign: "center" },
      },
      {
        label: "환산",
        style: { width: "55px", textAlign: "center" },
      },
      {
        label: "Retake/Senior",
        style: { width: "110px", textAlign: "center" },
      },
      {
        label: "적용과목명",
        style: { width: "170px", textAlign: "center" },
      },
      {
        label: "총점",
        style: { width: "70px", textAlign: "center" },
      },
      {
        label: "환산",
        style: { width: "55px", textAlign: "center" },
      },
      {
        label: "적용",
        style: { width: "70px", textAlign: "center" },
      },
    ],
    columns: [
      {
        valueKey: "lesson.subject_name",
        cellStyle: { textAlign: "left", paddingLeft: "8px" },
      },
      {
        valueKey: "lesson.lesson_hours",
        cellStyle: { textAlign: "center" },
      },
      {
        valueFormatter: (value, item) => {
          const teachers = item.lesson?.teachers;
          if (!teachers || !Array.isArray(teachers)) return "";
          return teachers
            .map((teacher) => teacher.tname || teacher.name || teacher)
            .join(", ");
        },
        cellStyle: { textAlign: "left", paddingLeft: "8px" },
      },
      {
        valueKey: "grade",
        cellStyle: { textAlign: "center" },
      },
      {
        valueKey: "convert100",
        cellStyle: { textAlign: "center" },
      },
      {
        valueFormatter: (value, item) => {
          const lessonId = item.lesson_id;
          const currentSetting = retakeSeniorSettings[lessonId] || {};
          const selectedType = currentSetting.type || "";

          return (
            <div className="text-center">
              <select
                className="form-select form-select-sm"
                value={selectedType || ""}
                onChange={(e) =>
                  handleRetakeSeniorSettingChange(
                    lessonId,
                    "type",
                    e.target.value
                  )
                }
                style={{ fontSize: "12px" }}
              >
                <option value="">선택</option>
                <option value="retake">Retake</option>
                <option value="senior">Senior</option>
              </select>
            </div>
          );
        },
        cellStyle: { textAlign: "center" },
      },
      {
        valueFormatter: (value, item) => {
          const lessonId = item.lesson_id;
          const currentSetting = retakeSeniorSettings[lessonId] || {};
          const subjectName = currentSetting.subject_name || "";

          return (
            <div className="text-center">
              <input
                type="text"
                className="form-control form-control-sm"
                value={subjectName || ""}
                onChange={(e) =>
                  handleRetakeSeniorSettingChange(
                    lessonId,
                    "subject_name",
                    e.target.value
                  )
                }
                style={{ fontSize: "12px" }}
                placeholder="과목명 입력"
              />
            </div>
          );
        },
        cellStyle: { textAlign: "center" },
      },
      {
        valueFormatter: (value, item) => {
          const lessonId = item.lesson_id;
          const currentSetting = retakeSeniorSettings[lessonId] || {};
          const grade = currentSetting.grade || "";

          return (
            <div className="text-center">
              <input
                type="number"
                className="form-control form-control-sm"
                value={grade || ""}
                onChange={(e) =>
                  handleRetakeSeniorSettingChange(
                    lessonId,
                    "grade",
                    e.target.value
                  )
                }
                style={{ fontSize: "12px", width: "70px" }}
                placeholder="점수"
                min="0"
                max="100"
              />
            </div>
          );
        },
        cellStyle: { textAlign: "center" },
      },
      {
        valueFormatter: (value, item) => {
          const lessonId = item.lesson_id;
          const currentSetting = retakeSeniorSettings[lessonId] || {};
          const convert100 = currentSetting.convert100 || "";

          return (
            <div className="text-center">
              <span
                className="badge bg-secondary"
                style={{ fontSize: "12px", padding: "4px 8px" }}
              >
                {convert100 || "-"}
              </span>
            </div>
          );
        },
        cellStyle: { textAlign: "center" },
      },
      {
        valueFormatter: (value, item) => {
          const lessonId = item.lesson_id;
          const isSaved = savedLessons.includes(lessonId);
          return (
            <div className="text-center">
              <button
                className={`btn ms-0 me-0 btn-sm ${
                  isSaved ? "btn-success" : "btn-primary"
                }`}
                onClick={() => handleSaveRetakeSeniorSetting(item)}
                style={{ fontSize: "12px", padding: "2px 4px" }}
              >
                {isSaved ? "저장완료" : "저장"}
              </button>
            </div>
          );
        },
        cellStyle: { textAlign: "center" },
      },
    ],
    rows: lessonStudentsArray,
  };

  return (
    <div className="p-4">
      {/* 헤더 */}
      <div className="row mb-4">
        <div className="col-auto">
          <h3 className="fw-bold">Retake/Senior Packet</h3>
        </div>
      </div>

      {/* 드롭다운 필터 섹션 */}
      <div
        className="d-flex justify-content-end gap-2 flex-wrap mb-4"
        style={{ width: "100%" }}
      >
        <div style={{ width: "95px" }} className="me-1">
          <DropdownSchoolYear
            settingData={newSetting}
            handleDropdownChange={handleDropdownChange}
            schoolYearId={filters.schoolYearId}
            allYears={false}
          />
        </div>
        <div style={{ width: "95px" }} className="me-1">
          <DropdownHierarchyOne
            settingData={settingData}
            handleDropdownChange={handleDropdownChange}
            schoolYear={filters.schoolYear}
            hierarchyOneId={filters.hierarchyOneId}
            extraOption={false}
          />
        </div>
        <div style={{ width: "95px" }} className="me-1">
          <DropdownHierarchyTwo
            settingData={settingData}
            handleDropdownChange={handleDropdownChange}
            schoolYear={filters.schoolYear}
            hierarchyOneId={filters.hierarchyOneId}
            hierarchyTwoId={filters.hierarchyTwoId}
            extraOption={false}
            disabled={!filters.hierarchyOneId}
          />
        </div>
        <div style={{ width: "95px" }} className="me-1">
          <DropdownHierarchyThree
            settingData={settingData}
            handleDropdownChange={handleDropdownChange}
            schoolYear={filters.schoolYear}
            hierarchyTwoId={filters.hierarchyTwoId}
            hierarchyThreeId={filters.hierarchyThreeId}
            extraOption={false}
            disabled={!filters.hierarchyTwoId}
          />
        </div>
        <div style={{ width: "120px" }} className="me-1">
          <DropdownUniqueUsersInDisplayData
            displayData={studentList}
            displayKey={"fullname"}
            valueKey={"username"}
            filters={{ organization_path: filters.organPathForSort }}
            uniqueKey={"username"}
            sortKey={"studentno"}
            isDesc={false}
            handleDropdownChange={handleDropdownChange}
            selectedUser={filters.selectedStudent}
            selectName={"students"}
            styleClass={"form-control"}
            dropdownTitle={t("common.user.student") || "학생"}
            required={true}
            disabled={!filters.hierarchyThreeId}
          />
        </div>
        <div style={{ width: "95px" }}>
          <DropdownTermDate
            termPeriodData={termPeriodData}
            handleDropdownChange={handleDropdownChange}
            termId={termId}
            schoolYearId={filters.schoolYearId}
          />
        </div>
      </div>
      {/* 테이블 */}
      {filters.selectedStudent ? (
        lessonStudentsArray.length > 0 ? (
          <div className="table-responsive">
            <DynamicTable
              tableConfig={tableConfig}
              dataStructure="flat"
              tableStyle="cleanModern"
              sortable={true}
              initialSortField="lesson.subject_name"
              initialSortDirection="asc"
            />
          </div>
        ) : (
          <div className="alert alert-info mt-4">
            <p>해당 학생의 수업 데이터가 없습니다.</p>
          </div>
        )
      ) : (
        <div className="alert alert-info mt-4">
          <h5>학생을 선택해주세요</h5>
          <p>
            학년도, 과정, 학년, 반, 학생을 순서대로 선택하면 해당 학생의
            Retake/Senior를 설정할 수 있습니다.
          </p>
        </div>
      )}
    </div>
  );
}
